import { useEffect } from "react";
import { getApiErrorMessage, isTargetMismatch, TARGET_MISMATCH_MESSAGE } from '../utils/apiErrors';
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toaster } from "baseui/toast";
import type { SAMUser } from "../types/role.types";
import * as samApi from "../api/samApi";
import { logApiError } from '../utils/logger';

/**
 * Hook for SAM user management operations
 * Provides functions for creating users, managing passwords, and listing all tenant users
 */

/**
 * Hook to fetch users in the current tenant.
 *
 * Cognito caps a single `list_users` call at 60 raw users, has no server-side
 * filter for our `custom:tenant_id` attribute, and its pagination token is
 * forward-only. So instead of one mega-call (which timed out at the API
 * Gateway 29s limit in prod), the backend now returns one Cognito page per
 * request and we walk forward here with `useInfiniteQuery`.
 *
 * Pages are eagerly chained: as soon as one resolves and `next_token` is
 * present, we fetch the next. The consumer sees `users` grow incrementally
 * while `isLoading`/`isFetchingMore` stays truthy until the last page lands.
 *
 * Pass `type` to filter server-side: 'admin' (users with any business_* group)
 * or 'app' (users without one). Omit to get both.
 */
/**
 * Whether the auto-chain asks for another page. A page that failed stops it: the flip of `isFetchingNextPage`
 * after the failure would otherwise fire the next request again, forever, against an endpoint that is failing.
 * `refetch` is the retry.
 */
export const shouldChainNextPage = (s: { hasNextPage: boolean; isFetchingNextPage: boolean; isFetchNextPageError: boolean }): boolean =>
  s.hasNextPage && !s.isFetchingNextPage && !s.isFetchNextPageError;

export const useAllTenantUsers = (type?: 'admin' | 'app', opts: { businessId?: string } = {}) => {
  const { businessId } = opts;
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    isError,
    isFetchNextPageError,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['sam-users', type ?? 'all', ...(businessId ? [businessId] : [])],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const response = await samApi.listAllTenantUsers({
        type,
        businessId,
        token: pageParam,
      }) as unknown as samApi.ListUsersResponse;
      return response;
    },
    getNextPageParam: (lastPage) => lastPage.next_token ?? undefined,
  });

  // Auto-chain: keep pulling pages until exhausted. This is what gives the
  // consumer "the full tenant set" without forcing them to wire a button.
  useEffect(() => {
    if (shouldChainNextPage({ hasNextPage, isFetchingNextPage, isFetchNextPageError })) {
      // A failed page is reported through isError; the rejected promise has nobody to handle it.
      fetchNextPage().catch(() => undefined);
    }
  }, [hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage]);

  const users: SAMUser[] = data?.pages.flatMap((p) => p.users) ?? [];

  return {
    users,
    // `isLoading` stays true until the *first* page arrives, then
    // `isFetchingMore` reports the background chaining. Consumers that just
    // want "is anything still in flight" can OR both.
    isLoading,
    isFetchingMore: isFetchingNextPage || hasNextPage,
    error,
    // SAM-277: expose the failure (first-load or a later page/refetch) plus a
    // manual retry, so the UI can distinguish "still loading" from "broken"
    // and tell stale cached data from a fresh, healthy list.
    isError,
    refetch,
  };
};

/**
 * SAM-222: normalize whatever shape the search endpoint returns into the same
 * flat `SAMUser` shape the list endpoint produces, so the table renders Name /
 * Email / Status / Groups identically whether a row came from the list or from
 * a search.
 *
 * It accepts: the flat list shape, a Cognito-style object with a
 * `UserAttributes` / `Attributes` array, and a `custom_attributes` bag. NOTE:
 * the current backend exact-match path drops the standard attributes entirely
 * (samUserManagement `format_user_data` reads `Attributes`, but
 * `admin_get_user` returns `UserAttributes`), so email/name are absent from the
 * response and cannot be recovered here — that root cause must be fixed in the
 * backend. This normalizer makes the frontend correct for every shape and will
 * populate the columns as soon as the backend includes the attributes.
 */
export const normalizeSAMUser = (raw: any): SAMUser => {
  if (!raw || typeof raw !== 'object') return raw;

  const attrList = raw.UserAttributes || raw.Attributes;
  const attrs: Record<string, string> = Array.isArray(attrList)
    ? Object.fromEntries(attrList.map((a: any) => [a.Name, a.Value]))
    : {};
  const custom = raw.custom_attributes || {};

  return {
    username: raw.username || raw.Username || attrs['email'] || '',
    status: raw.status || raw.UserStatus || '',
    created: raw.created || raw.UserCreateDate || '',
    last_modified: raw.last_modified || raw.UserLastModifiedDate || '',
    enabled: raw.enabled ?? raw.Enabled ?? true,
    email: raw.email || attrs['email'] || '',
    name: raw.name || attrs['name'] || '',
    family_name: raw.family_name || attrs['family_name'] || '',
    phone_number: raw.phone_number || attrs['phone_number'] || undefined,
    tenant_id: raw.tenant_id || attrs['custom:tenant_id'] || custom['tenant_id'] || '',
    groups: Array.isArray(raw.groups) ? raw.groups : [],
    sub: raw.sub || attrs['sub'],
    custom_attributes: raw.custom_attributes,
  } as SAMUser;
};

/**
 * Hook to search users by query
 */
export const useSearchUsers = (query: string, enabled: boolean = true) => {
  const { data, isLoading, error, isError, refetch } = useQuery({
    queryKey: ['sam-users-search', query],
    queryFn: async () => {
      const response = await samApi.searchUsers(query) as any;

      // Handle both single user and array responses, normalizing each so the
      // table columns render identically to the unfiltered list (SAM-222).
      let rawUsers: any[] = [];
      if (response.user) {
        // Single user response: { "user": {...} }
        rawUsers = [response.user];
      } else if (response.users) {
        // Multiple users response: { "users": [...] }
        rawUsers = response.users;
      }

      return rawUsers.map(normalizeSAMUser);
    },
    enabled: enabled && query.length > 0,
    retry: false, // Don't retry on error
    refetchOnWindowFocus: false, // Don't refetch when window gains focus
  });

  const users: SAMUser[] = data || [];

  return {
    users,
    isLoading,
    error,
    // SAM-213: expose the failure so the UI can distinguish "search failed"
    // (retryable) from "no results".
    isError,
    refetch,
  };
};

/**
 * Hook to create a new user
 */
export const useCreateUser = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (userData: samApi.CreateUserRequest) => {
      return await samApi.createUser(userData) as unknown as samApi.CreateUserResponse;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['sam-users'] });
      queryClient.invalidateQueries({ queryKey: ['sam-group-users'] });
      toaster.positive(`User ${data.user.username} created successfully`, {
        autoHideDuration: 3000,
      });
    },
    onError: (error: any) => {
      logApiError('Error creating user:', error);
      const errorMessage = getApiErrorMessage(error, "Error creating user");
      toaster.negative(errorMessage, {
        autoHideDuration: 5000,
      });
    },
  });

  return {
    createUser: mutation.mutate,
    createUserAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error,
  };
};

/**
 * Hook to reset a user's password
 */
export const useResetPassword = (expectedSub?: string | null) => {
  const mutation = useMutation({
    mutationFn: async ({ username, options }: { username: string; options: samApi.ResetPasswordRequest }) => {
      return await samApi.resetPassword(username, options, expectedSub) as unknown as samApi.GenericMessageResponse;
    },
    onSuccess: (data) => {
      toaster.positive(data.message || "Password reset successfully", {
        autoHideDuration: 3000,
      });
    },
    onError: (error: any) => {
      logApiError('Error resetting password:', error);
      const errorMessage = isTargetMismatch(error) ? TARGET_MISMATCH_MESSAGE : error?.response?.data?.error || error?.message || "Error resetting password";
      toaster.negative(errorMessage, {
        autoHideDuration: 5000,
      });
    },
  });

  return {
    resetPassword: (username: string, options: samApi.ResetPasswordRequest) => 
      mutation.mutate({ username, options }),
    isResetting: mutation.isPending,
    error: mutation.error,
  };
};

/**
 * Hook to change a user's password
 */
export const useChangePassword = (expectedSub?: string | null) => {
  const mutation = useMutation({
    mutationFn: async ({ username, newPassword }: { username: string; newPassword: string }) => {
      return await samApi.changePassword(username, newPassword, expectedSub) as unknown as samApi.GenericMessageResponse;
    },
    onSuccess: (data) => {
      toaster.positive(data.message || "Password changed successfully", {
        autoHideDuration: 3000,
      });
    },
    onError: (error: any) => {
      logApiError('Error changing password:', error);
      const errorMessage = isTargetMismatch(error) ? TARGET_MISMATCH_MESSAGE : error?.response?.data?.error || error?.message || "Error changing password";
      toaster.negative(errorMessage, {
        autoHideDuration: 5000,
      });
    },
  });

  return {
    changePassword: (username: string, newPassword: string) => 
      mutation.mutate({ username, newPassword }),
    isChanging: mutation.isPending,
    error: mutation.error,
  };
};

/**
 * Hook to disable a user
 */
export const useDisableUser = (expectedSub?: string | null) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (username: string) => {
      return await samApi.disableUser(username, expectedSub) as unknown as samApi.GenericMessageResponse;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['sam-users'] });
      toaster.positive(data.message || "User disabled successfully", {
        autoHideDuration: 3000,
      });
    },
    onError: (error: any) => {
      const errorMessage = isTargetMismatch(error) ? TARGET_MISMATCH_MESSAGE : error?.response?.data?.error || error?.message || "Error disabling user";
      toaster.negative(errorMessage, { autoHideDuration: 5000 });
    },
  });

  return {
    disableUser: mutation.mutate,
    isDisabling: mutation.isPending,
  };
};

/**
 * Hook to enable a user
 */
export const useEnableUser = (expectedSub?: string | null) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (username: string) => {
      return await samApi.enableUser(username, expectedSub) as unknown as samApi.GenericMessageResponse;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['sam-users'] });
      toaster.positive(data.message || "User enabled successfully", {
        autoHideDuration: 3000,
      });
    },
    onError: (error: any) => {
      const errorMessage = isTargetMismatch(error) ? TARGET_MISMATCH_MESSAGE : error?.response?.data?.error || error?.message || "Error enabling user";
      toaster.negative(errorMessage, { autoHideDuration: 5000 });
    },
  });

  return {
    enableUser: mutation.mutate,
    isEnabling: mutation.isPending,
  };
};

/**
 * Hook to delete a user (must be disabled first).
 *
 * On success, removes the user from every `['sam-users', ...]` infinite-query
 * cache *optimistically* (instant UI update) and then invalidates so the next
 * natural refetch re-syncs. The optimistic step is required because Cognito's
 * `list_users` / `list_users_in_group` have eventual consistency — for several
 * seconds after `admin_delete_user`, those listings still return the deleted
 * user. Without the optimistic remove the operator sees the user "come back"
 * until Cognito reconciles.
 */
export const useDeleteUser = (expectedSub?: string | null) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (username: string) => {
      return await samApi.deleteUser(username, expectedSub) as unknown as samApi.GenericMessageResponse;
    },
    onSuccess: (data, username) => {
      queryClient.setQueriesData(
        { queryKey: ['sam-users'] },
        (old: any) => {
          if (!old?.pages) return old;
          return {
            ...old,
            pages: old.pages.map((page: samApi.ListUsersResponse) => {
              const users = (page.users || []).filter((u) => u.username !== username);
              return { ...page, users, count: users.length };
            }),
          };
        }
      );
      // Mark stale without forcing an immediate refetch: Cognito needs a few
      // seconds to make the deleted user disappear from listings. Next window
      // focus / page nav will pick up the canonical state.
      queryClient.invalidateQueries({ queryKey: ['sam-users'], refetchType: 'none' });
      toaster.positive(data.message || "User deleted successfully", {
        autoHideDuration: 3000,
      });
    },
    onError: (error: any) => {
      const errorMessage = isTargetMismatch(error) ? TARGET_MISMATCH_MESSAGE : error?.response?.data?.error || error?.message || "Error deleting user";
      toaster.negative(errorMessage, { autoHideDuration: 5000 });
    },
  });

  return {
    deleteUser: mutation.mutate,
    isDeleting: mutation.isPending,
  };
};
